"use client";

import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { calculateWeightInsightsAtDate } from "@/lib/weight-calculations";
import { NoteAnswerDetail } from "@/types/notes";
import { WeightInsights } from "./weight-insights";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";
import { patientQueryOptions } from "@/api/provider/user/patient/patient-query";
interface WeightAnswerRendererProps {
  answer: NoteAnswerDetail;
  noteId?: string;
  patientId?: string;
}
export const WeightAnswerRenderer: React.FC<WeightAnswerRendererProps> = ({
  answer,
  noteId,
  patientId
}) => {
  const {
    data
  } = answer;

  // Fetch patient data to get measurements
  const {
    data: patient,
    isLoading: isLoadingPatient
  } = useQuery({
    ...patientQueryOptions.detail(patientId || ""),
    enabled: !!patientId
  });

  // Fetch note detail to get createdDate for reference
  const {
    data: noteDetail,
    isLoading: isLoadingNote
  } = useQuery({
    ...noteQueryOptions.detail(noteId || ""),
    enabled: !!noteId
  });

  // Handle both old format {pounds: number} and new format {value: number}
  const rawWeight = data.value ?? ("pounds" in data ? data.pounds : null);
  const weightValue = rawWeight !== null && rawWeight !== undefined ? Number(rawWeight) : null;

  // Days are compared on the clinic clock, so insights follow its zone.
  const clock = useClinicClock();
  const weightInsights = useMemo(() => {
    if (!patient?.measurements || weightValue === null) {
      return null;
    }

    // Use note's createdDate as reference, or current date for new notes
    const referenceDate = noteDetail?.createdDate ? new Date(noteDetail.createdDate) : new Date();
    return calculateWeightInsightsAtDate(patient.measurements, weightValue, referenceDate, clock);
  }, [patient?.measurements, weightValue, noteDetail?.createdDate, clock]);
  const isLoadingInsights = isLoadingPatient || isLoadingNote;
  const showInsights = !!patientId && weightValue !== null;
  if (weightValue === null) {
    return <div className="text-body text-foreground">—</div>;
  }
  return <div className="flex items-center gap-3">
      <span className="shrink-0 text-body text-foreground">
        {weightValue} lbs
      </span>
      {showInsights && <WeightInsights changeSinceLastVisit={weightInsights?.changeSinceLastVisit ?? null} totalWeightChange={weightInsights?.totalWeightChange ?? null} previousDate={weightInsights?.previousMeasurementDate ?? null} firstDate={weightInsights?.firstMeasurementDate ?? null} isLoading={isLoadingInsights} />}
    </div>;
};